import { DEGREE_90_IN_RAD, SHAPE_STYLES, TEXT_STYLE } from './constants';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  anchoredPos,
  createTransformSizeAnimation,
  getEntityStateAndProgress,
  getZeroEntity,
  getZeroStaticEntity,
  setParent,
} from './entity';
import {
  clamp01,
  Ease,
  getCornerFromRect,
  subtractV2,
  toSize,
  v2,
} from './helpers';
import {
  EventClientToServer,
  JSON_OpenRoomNode,
  RoomNodeStyle,
  ServerEvent_EntityProps,
  ServerEvent_RoomNode,
} from './shared/api_shared_types';
import { setVisibilityState, setParentByEntityProps } from './simulate';
import { createAndAddLocalTextObject } from './textobject/textobject';
import {
  AnimationType,
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  MoveMode,
  Particle,
  RenderItemType,
  RoomNode,
  Shape,
  SharedObjectType,
  TextAppearStyle,
  type V2,
} from './types';
import { dt } from './index';
import { createImageEnt } from './image_entity';
import { getZeroParticle } from './particle_system';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageCircle,
  type RenderPackageItem,
  type RenderPackageRect,
  type RenderPackageShape,
} from './render_package';

export function controller(): GameObjectController {
  return {
    getNew: getZeroRoomNode,
    sync,
    update,
    createPackage,
  };
}

export function getZeroRoomNode(ent: Entity): RoomNode {
  let newRoomNode: RoomNode = {
    ...ent,
    objectType: SharedObjectType.ROOM_NODE,
    highlightProcess: 0,
    locked: false,
    label: '',
    infoMsg: '',
    iconId: '',
    tObjLabel: null,
    tObjInfo: null,
    imgLock: null,
    img: null,
    imgHover: null,
    style: RoomNodeStyle.NONE,
  };

  return newRoomNode;
}

export function sync(manager: GameManager, roomNode: RoomNode, json: ServerEvent_RoomNode) {
  let entityProps: ServerEvent_EntityProps = {};

  if (json.hasOwnProperty('entityProps')) {
    entityProps = json.entityProps;
  }

  if (json.hasOwnProperty('locked')) {
    roomNode.locked = json.locked;
  }

  if (json.hasOwnProperty('infoMsg')) {
    roomNode.infoMsg = json.infoMsg;
  }

  if (json.hasOwnProperty('style')) {
    roomNode.style = json.style;
  }

  if (!roomNode.id) {
    addGameObject(manager, roomNode);
    initRoomNode(manager, roomNode, json.label, json.iconId);
    setParentByEntityProps(manager, roomNode, json.sId, json.roomId, entityProps);
    roomNode.serverObjId = json.sId;
  }

  // setGameObjectyEntityProps(roomNode, entityProps);
}

export function initRoomNode(manager: GameManager, node: RoomNode, label: string, iconId: string) {
  node.label = label;
  node.iconId = iconId;

  node.shape = node.style === RoomNodeStyle.CIRCLE ? Shape.CIRCLE : Shape.RECT;
  node.moveMode = MoveMode.KINETIC;
  node.influenceKinetic = true;

  node.clickable = true;
  node.allowDrag = true;
  node.iconId = iconId;

  if (
    false &&
    (label === 'Sunrise' || label === 'Community' || label === 'Not my typical profile')
  ) {
    {
      let imageId = '';
      if (label === 'Sunrise') {
        imageId = 'menuSunrise';
      } else if (label === 'Community') {
        imageId = 'menuCommunity';
      } else if (label === 'Not my typical profile') {
        imageId = 'menuNMTP';
      }

      node.img = createImageEnt(manager, getZeroStaticEntity(), imageId);
      node.img.maxHeightEm = 1.5;
      node.img.clickable = true;
      node.img.influenceKinetic = true;
      node.img.allowHighlight = true;
      node.img.allowDrag = true;
      node.img.passClick = true;
      node.img.passHover = true;
      node.img.passDrag = true;
      setParent(node.img, node);
    }
    {
      let imageId = '';
      if (label === 'Sunrise') {
        imageId = 'menuSunriseHover';
      } else if (label === 'Community') {
        imageId = 'menuCommunityHover';
      } else if (label === 'Not my typical profile') {
        imageId = 'menuNMTPHover';
      }

      node.imgHover = createImageEnt(manager, getZeroStaticEntity(), imageId);
      node.imgHover.maxHeightEm = 1.5;
      node.imgHover.clickable = true;
      node.imgHover.allowHighlight = true;
      node.imgHover.allowDrag = true;
      node.imgHover.passClick = true;
      node.imgHover.passHover = true;
      node.imgHover.passDrag = true;
      node.imgHover.alpha = 0;
      setParent(node.imgHover, node);
    }
  } else {
    {
      let style = TEXT_STYLE.ROOM_NODE;
      let maxWidthEm = 3;

      if (node.style === RoomNodeStyle.BIG) {
        style = node.locked ? TEXT_STYLE.ROOM_NODE_BIG_LOCKED : TEXT_STYLE.ROOM_NODE_BIG;
        maxWidthEm = 3;
      } else if (node.style === RoomNodeStyle.CIRCLE) {
        style = TEXT_STYLE.ROOM_NODE_CIRCLE;
        maxWidthEm = 1.5;
      } else {
        style = node.locked ? TEXT_STYLE.ROOM_NODE_LOCKED : TEXT_STYLE.ROOM_NODE;
        maxWidthEm = 3;
      }
      node.tObjLabel = createAndAddLocalTextObject(
        manager,
        getZeroEntity(),
        label,
        style,
        maxWidthEm,
        false,
        0,
        1,
        TextAppearStyle.FADE,
      );
      setParent(node.tObjLabel, node, v2());
      node.tObjLabel.passClick = true;
      node.tObjLabel.passHover = true;
      node.tObjLabel.passDrag = true;

      node.tObjLabel.moveMode = MoveMode.ANCHORED;
      if (node.style === RoomNodeStyle.CIRCLE) {
        node.tObjLabel.anchoredPos = anchoredPos();
      } else {
        node.tObjLabel.anchoredPos = anchoredPos(v2(0.5, 0), v2(0, 0), v2(0.5, 0));
      }
    }

    if (node.locked) {
      node.imgLock = createImageEnt(manager, getZeroStaticEntity(), 'iconLocked');
      node.imgLock.maxHeightEm = 0.5;
      setParent(node.imgLock, node);

      setVisibilityState(node.imgLock, VisibilityState.APPEARING);
      node.imgLock.moveMode = MoveMode.ANCHORED;
      node.imgLock.anchoredPos = anchoredPos(v2(0.5, 0), v2(0, 10), v2(0.5, 0));
    }

    if (node.infoMsg) {
      const maxWidthEm = node.shape === Shape.RECT ? 3 : 1.9;

      node.tObjInfo = createAndAddLocalTextObject(
        manager,
        getZeroEntity(),
        node.infoMsg,
        TEXT_STYLE.ROOM_NODE_INFO,
        maxWidthEm,
      );
      setParent(node.tObjInfo, node, v2());
      node.tObjInfo.passClick = true;
      node.tObjInfo.passHover = true;
      node.tObjInfo.passDrag = true;

      node.tObjInfo.moveMode = MoveMode.ANCHORED;
      node.tObjInfo.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, 30), v2(0.5, 0));
      node.tObjInfo.onlyShowInDefaultState = false;
      node.tObjInfo.appearStyle = TextAppearStyle.FADE_AND_MOVE_UP;
      setVisibilityState(node.tObjInfo, VisibilityState.HIDDEN);
    }

    node.height = node.tObjLabel.height;
    node.width = node.tObjLabel.width;
    node.radius = (node.tObjLabel.width / 2) * 1.3;
  }

  setVisibilityState(node, VisibilityState.APPEARING);
}

function update(node: RoomNode): boolean {
  let { state, progress } = getEntityStateAndProgress(node);
  if (state === VisibilityState.APPEARING) {
    node.scale = Ease.outBack(progress);
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    node.scale = 1 - Ease.inBack(progress);
  } else if (node.scale !== 1) {
    const p: Particle = {
      ...getZeroParticle(),
      shape: Shape.CIRCLE,
      size: node.radius,
      maxLifeTime: 0.5,
    };
    node.scale = 1;
  }

  if (node.tObjLabel.layoutChanged) {
    node.height = node.tObjLabel.height;
    node.width = node.tObjLabel.width;
  }

  if (node.img) {
    const SPEED = 5;
    const DIFF = dt * SPEED;
    if (node.highlighted) {
      if (node.imgHover.alpha < 1) {
        const newTransparancy = node.imgHover.alpha + DIFF;
        node.imgHover.alpha = clamp01(newTransparancy);
      } else if (node.img.alpha > 0) {
        const newTransparancy = node.img.alpha - DIFF;
        node.img.alpha = clamp01(newTransparancy);
      }
    } else {
      if (node.img.alpha < 1) {
        const newTransparancy = node.img.alpha + DIFF;
        node.img.alpha = clamp01(newTransparancy);
      } else if (node.imgHover.alpha > 0) {
        const newTransparancy = node.imgHover.alpha - DIFF;
        node.imgHover.alpha = clamp01(newTransparancy);
      }
    }
    if (node.width !== node.img.width) {
      node.width = node.img.width;
      node.height = node.img.height;
      // node.widthScale = 1;
      // node.heightScale = 1;
    }
  } else {
    if (node.highlighted && !node.highlightLastFrame && node.locked && node.infoMsg) {
      showInfo(node);
    } else if (!node.highlighted && node.highlightLastFrame && node.locked && node.infoMsg) {
      hideInfo(node);
    }
  }

  return false;
}

function showInfo(roomNode: RoomNode) {
  const SIZE_HOVER = 240;
  const w = roomNode.shape === Shape.RECT ? roomNode.tObjInfo.width : SIZE_HOVER;
  const h =
    roomNode.shape === Shape.RECT
      ? roomNode.tObjLabel.height + roomNode.tObjInfo.height + 30
      : SIZE_HOVER;

  createTransformSizeAnimation(roomNode.manager, roomNode, {
    type: AnimationType.TRANSFORM_SIZE,
    end: {
      sizePx: toSize(w, h),
      sizeEm: null,
      // sizeScale: toSize(roomNode.widthScale, roomNode.heightScale),
    },
    duration: 0.2,
    easeFunction: Ease.inOutQuad,
  });

  if (roomNode.tObjLabel) {
    setVisibilityState(roomNode.tObjInfo, VisibilityState.APPEARING);
  }
}

function hideInfo(roomNode: RoomNode) {
  const h = roomNode.tObjLabel.height;

  createTransformSizeAnimation(roomNode.manager, roomNode, {
    type: AnimationType.TRANSFORM_SIZE,
    end: {
      sizePx: toSize(roomNode.tObjLabel.width, h),
      sizeEm: null,
      // sizeScale: toSize(roomNode.widthScale, roomNode.heightScale),
    },
    duration: 0.2,
    easeFunction: Ease.inOutQuad,
  });

  if (roomNode.tObjLabel) {
    setVisibilityState(roomNode.tObjInfo, VisibilityState.HIDDEN);
  }
}

export function createPackage(
  roomNode: RoomNode,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const w = roomNode.img ? roomNode.img.width : roomNode.width;
  const h = roomNode.img ? roomNode.img.height : roomNode.height;

  if (roomNode.style === RoomNodeStyle.CIRCLE) {
    let style = SHAPE_STYLES.SOCIAL_ROOM_NODE;
    if (roomNode.highlighted) {
      style = SHAPE_STYLES.SOCIAL_ROOM_NODE_HOVER;
    }

    let circle: RenderPackageCircle = {
      type: RenderItemType.circle,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      radius: roomNode.radius,
      shapeStyle: style,
      shadow: null,
    };
    rPackage.push(circle);
  } else {
    if (roomNode.highlighted) {
      let rect: RenderPackageRect = {
        type: RenderItemType.rect,
        ...EMPTY_RENDER_TRANSFORM,
        scale,
        positionX: position.x,
        positionY: position.y,
        width: w,
        height: h,
        shapeStyle: SHAPE_STYLES.ROOM_NODE_RING_1,
        cornerRadius1: 0,
        cornerRadius2: 0,
        cornerRadius3: 0,
        cornerRadius4: 0,
        shadow: null,
      };
      rPackage.push(rect);
    } else {
      const CORNER_SIZE = 0.2 * roomNode.manager.rootUnitSizePx;
      for (let i = 0; i < 4; i++) {
        const corner = i;
        const cornerPos = getCornerFromRect(w, h, corner);
        let shape: RenderPackageShape = {
          ...EMPTY_RENDER_TRANSFORM,
          type: RenderItemType.shape,
          positionX: position.x + scale * cornerPos.x,
          positionY: position.y + scale * cornerPos.y,
          rotation: i * DEGREE_90_IN_RAD,
          points: [v2(0, scale * CORNER_SIZE), v2(), v2(scale * CORNER_SIZE, 0)],
          close: false,
          fill: false,
          shapeStyle: {
            ...SHAPE_STYLES.NONE,
            strokeWeight: 2 * scale,
            strokeColor: COLORS.WHITE_50,
          },
          shadow: null,
        };
        rPackage.push(shape);
      }
    }
  }

  return rPackage;
}
